import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import axios from 'axios';
import { startGoogleAuth, useAuth } from '../lib/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SignIn() {
  const { user, loading } = useAuth();
  const [msConfigured, setMsConfigured] = React.useState(null);

  React.useEffect(() => {
    axios.get(`${API}/auth/microsoft/config`).then(({ data }) => {
      setMsConfigured(!!data?.configured);
    }).catch(() => setMsConfigured(false));

    // Surface an OAuth error redirect (?error=microsoft&msg=…)
    const p = new URLSearchParams(window.location.search);
    if (p.get('error') === 'microsoft') {
      toast.error(`Microsoft sign-in failed: ${p.get('msg') || 'unknown error'}`);
    }
  }, []);

  const startMicrosoft = () => {
    if (msConfigured) {
      window.location.href = `${API}/auth/microsoft/start?return_to=${encodeURIComponent('/profile')}`;
    } else {
      toast.info('Microsoft sign-in is launching soon — Google works today.');
    }
  };

  // If already signed in, bounce straight into the app
  if (loading) {
    return <div className="min-h-[70vh] grid place-items-center" data-testid="signin-loading"><Loader2 className="w-8 h-8 text-[#ff2a2a] animate-spin"/></div>;
  }
  if (user) return <Navigate to="/profile" replace />;

  return (
    <div className="min-h-[calc(100vh-4rem)] grid lg:grid-cols-2" data-testid="page-signin">
      <div className="hidden lg:block relative">
        <img src="https://images.pexels.com/photos/9827685/pexels-photo-9827685.jpeg" alt="cars" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/20" />
        <div className="relative p-12 h-full flex flex-col justify-end">
          <div className="font-display text-5xl uppercase tracking-tight leading-none">Your ride.<br />Your rules.</div>
          <p className="mt-3 text-zinc-300 max-w-md">Sign in to save listings, get price-drop alerts, and manage your subscription.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-8 lg:p-16">
        <div className="w-full max-w-md">
          <div className="font-mono text-xs uppercase tracking-widest text-[#ff2a2a] mb-2">Sign in</div>
          <h1 className="font-display text-4xl uppercase tracking-tight leading-none">Welcome back.</h1>
          <p className="mt-3 text-sm text-zinc-400">Use your Google or Microsoft account — we don&rsquo;t ask for a password.</p>

          {/* Social sign-in */}
          <div className="mt-8 space-y-2" data-testid="social-signin">
            <button
              onClick={startGoogleAuth}
              className="w-full bg-white text-black text-sm font-semibold py-3 hover:bg-zinc-100 transition-colors inline-flex items-center justify-center gap-3"
              data-testid="signin-google"
            >
              <svg viewBox="0 0 48 48" className="w-4 h-4" aria-hidden>
                <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.5l6.8-6.8C35.9 2.4 30.4 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.9 6.1C12.4 13.5 17.7 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3.1-2.4 5.7-5 7.5l7.7 6c4.5-4.1 7.1-10.2 7.1-18z"/>
                <path fill="#FBBC05" d="M10.5 28.7c-.5-1.4-.7-3-.7-4.7s.3-3.3.7-4.7l-7.9-6.1C1 16.4 0 20.1 0 24s1 7.6 2.6 10.8l7.9-6.1z"/>
                <path fill="#34A853" d="M24 48c6.4 0 11.9-2.1 15.9-5.8l-7.7-6c-2.1 1.4-4.9 2.3-8.2 2.3-6.3 0-11.6-4-13.5-9.5l-7.9 6.1C6.5 42.6 14.6 48 24 48z"/>
              </svg>
              Continue with Google
            </button>

            <button
              onClick={startMicrosoft}
              className="w-full hairline hover:border-white/30 text-white text-sm font-semibold py-3 transition-colors inline-flex items-center justify-center gap-3"
              data-testid="signin-microsoft"
            >
              <svg viewBox="0 0 23 23" className="w-4 h-4" aria-hidden>
                <rect x="1"  y="1"  width="10" height="10" fill="#F25022"/>
                <rect x="12" y="1"  width="10" height="10" fill="#7FBA00"/>
                <rect x="1"  y="12" width="10" height="10" fill="#00A4EF"/>
                <rect x="12" y="12" width="10" height="10" fill="#FFB900"/>
              </svg>
              Continue with Microsoft
              {msConfigured === false && (
                <span className="am-pill ml-1 !py-0.5 !px-1.5 !text-[9px]">Soon</span>
              )}
            </button>
          </div>

          <div className="mt-8 space-y-3 text-xs text-zinc-500 leading-relaxed">
            <div className="flex items-start gap-2">
              <span className="text-[#ff2a2a] mt-0.5">·</span>
              <span>No password to remember or leak. Google/Microsoft handles the sign-in and we get a verified email.</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-[#ff2a2a] mt-0.5">·</span>
              <span>Your name and photo come across so buyers can trust you. Everything else stays with you.</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-[#ff2a2a] mt-0.5">·</span>
              <span>Sign out anytime from your profile.</span>
            </div>
          </div>

          <div className="mt-8 text-[10px] font-mono uppercase tracking-widest text-zinc-600 text-center">
            By continuing you agree to our <Link to="/terms" className="hover:text-white">Terms</Link>. PIPEDA-compliant.
          </div>
        </div>
      </div>
    </div>
  );
}
